<template>
  <div class="search-container">
    <!-- 搜索区域 -->
    <div class="search-bar">
      <el-input v-model="searchName" placeholder="请输入姓名" class="search-input" @keyup.enter="handleSearch">
      </el-input>
      <el-button type="primary" @click="handleSearch" :loading="loading" class="search-button">
        搜索
      </el-button>
      <el-button type="success" @click="openAddDialog" class="add-button">
        新增
      </el-button>
    </div>

    <!-- 搜索结果展示 -->
    <div v-if="loading" class="loading-container">
      <el-loading text="搜索中..." />
    </div>
    <div v-if="!loading && searchResult" class="result-container">
      <el-card shadow="hover" class="result-card">
        <el-image class="result-photo" :src="searchResult.photo" fit="cover" />
        <div class="result-info">
          <h2>{{ searchResult.name }}</h2>
          <p>
            <strong>性别：</strong>
            <!-- 后台返回中 gender 为数字 -->
            <el-tag :type="searchResult.gender == 1 ? 'success' : 'info'" class="info-tag">
              {{ searchResult.gender == 1 ? '男' : '女' }}
            </el-tag>
          </p>
          <p><strong>出生年月日：</strong>{{ searchResult.birth }}</p>
          <p><strong>身份证号：</strong>{{ maskedIdCard }}</p>
          <p><strong>户籍：</strong>{{ searchResult.residence }}</p>
          <p>
            <strong>目前状态：</strong>
            <el-tag :style="{ backgroundColor: statusTagType() }" class="status-tag">
              {{ searchResult.status }}
            </el-tag>

            <!-- 查看详情弹窗按钮 -->
            <el-button type="primary" class="detail-button" @click="openDetailDialog">
              查看详情
            </el-button>
          </p>
          <p><strong>创建时间：</strong>{{ searchResult.create_time }}</p>
        </div>
      </el-card>
    </div>
    <div v-else-if="searched && !searchResult" class="empty-container">
      <el-empty description="未找到相关数据"></el-empty>
    </div>

    <!-- 新增申请弹窗（保持原有逻辑） -->
    <el-dialog v-model="showAddDialog" title="新增申请" width="60%" :close-on-click-modal="false">
      <el-form :model="addForm" ref="addFormRef" label-width="120px">
        <el-form-item label="姓名">
          <el-input v-model="addForm.name" placeholder="请输入姓名" />
        </el-form-item>
        <el-form-item label="照片">
          <el-upload action="" :before-upload="() => false" :on-change="(file) => handlePhotoUpload(file.raw)"
            show-file-list="false">
            <el-button type="primary" :loading="uploading">上传照片</el-button>
          </el-upload>
          <img v-if="addForm.photo" :src="addForm.photo" alt="照片预览" style="height: 200px;width: 200px;"
            class="photo-preview" />
        </el-form-item>
        <el-form-item label="性别">
          <el-radio-group v-model="addForm.gender">
            <el-radio :label="1">男</el-radio>
            <el-radio :label="0">女</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="出生日期">
          <el-date-picker v-model="addForm.birth" type="date" placeholder="选择日期" value-format="YYYY-MM-DD" />
        </el-form-item>
        <el-form-item label="身份证号">
          <el-input v-model="addForm.idCard" placeholder="请输入身份证号" />
        </el-form-item>
        <el-form-item label="住址">
          <el-input v-model="addForm.residence" placeholder="请输入住址" />
        </el-form-item>
        <el-form-item label="申请阶段">
          <!-- 新增申请弹窗中使用独立的 newStageDetails -->
          <el-tabs v-model="activeAddStage" @tab-click="handleAddStageTabChange" tab-position="left">
            <el-tab-pane v-for="stage in stageList" :key="stage.id" :label="stage.name" :name="stage.id.toString()">
              <div v-if="newStageDetails[stage.id]">
                <div v-for="detail in newStageDetails[stage.id]" :key="detail.id" class="detail-upload">
                  <el-form-item :label="detail.name" :label-width="'120px'" :style="{ color: detail.color }">
                    <!-- 上传操作：调用 handleFileChange 保存到 addForm.stages -->
                    <el-upload action="" :before-upload="() => false"
                      :on-change="(file) => handleFileChange(stage.id, detail.id, file.raw)" show-file-list="false">
                      <el-button type="primary" :loading="uploading"
                        :style="{ backgroundColor: detail.color, borderColor: detail.color }">
                        上传 {{ detail.name }}
                      </el-button>
                    </el-upload>
                    <span v-if="getUploadedFile(stage.id, detail.id)" class="uploaded-file">已上传</span>
                  </el-form-item>
                </div>
              </div>
              <div v-else>
                加载中...
              </div>
            </el-tab-pane>
          </el-tabs>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showAddDialog = false">取消</el-button>
          <el-button type="primary" @click="submitApplication">提交</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 查看详情弹窗 -->
    <el-dialog v-model="showDetailDialog" title="申请详情" width="80%" :close-on-click-modal="false">
      <div class="detail-dialog-content">
        <!-- 使用独立的 viewStageDetails 加载查看详情用子过程定义 -->
        <el-tabs v-model="activeViewStage" @tab-click="handleViewStageTabChange">
          <el-tab-pane v-for="stage in stageList" :key="stage.id" :name="stage.id.toString()">
            <template #label>
              <!-- 如果该阶段所有子过程都已上传，则加上 stage-completed 类和图标 -->
              <span :class="{ 'stage-completed': isStageComplete(stage.id) }">{{ stage.name }}</span>
              <el-icon v-if="isStageComplete(stage.id)" class="completed-icon">
                <Check />
              </el-icon>
            </template>
            <div v-if="viewStageDetails[stage.id]">
              <div v-for="detail in viewStageDetails[stage.id]" :key="detail.id" class="detail-upload">
                <el-form-item :label="detail.name" :label-width="'150px'" :style="{ color: detail.color }">
                  <template v-if="hasUploaded(stage.id, detail.name)">
                    <div class="button-group">
                      <el-button type="primary" @click="openImage(getUploaded(stage.id, detail.name))">
                        {{ detail.name }} 已上传
                      </el-button>
                      <el-upload action="" :before-upload="() => false"
                        :on-change="(file) => handleViewFileUpdate(stage.id, detail.name, detail.name, file.raw)"
                        show-file-list="false">
                        <el-button type="primary" :loading="uploading" size="mini"
                          class="completed-button">修改</el-button>
                      </el-upload>
                    </div>
                  </template>
                  <template v-else>
                    <el-upload action="" :before-upload="() => false"
                      :on-change="(file) => handleViewFileUpdate(stage.id, detail.name, detail.name, file.raw)"
                      show-file-list="false">
                      <el-button type="default" :loading="uploading" :style="{ backgroundColor: '#ccc' }">
                        {{ detail.name }} 未上传
                      </el-button>
                    </el-upload>
                  </template>
                </el-form-item>
              </div>
            </div>
            <div v-else>
              加载中...
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showDetailDialog = false">关闭</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 图片/文件预览弹窗 -->
    <el-dialog v-model="showImageDialog" width="600px" :close-on-click-modal="false" custom-class="image-dialog"
      title="预览">
      <div class="image-preview-container">
        <template v-if="fileType === 'image'">
          <img :src="currentImage" alt="预览图片" class="preview-img" />
        </template>
        <template v-else-if="fileType === 'video'">
          <video class="preview-video" controls :src="currentImage"></video>
        </template>
        <template v-else-if="fileType === 'audio'">
          <audio class="preview-audio" controls :src="currentImage"></audio>
        </template>
        <template v-else-if="fileType === 'pdf'">
          <iframe class="preview-iframe" :src="currentImage" frameborder="0"></iframe>
        </template>
        <template v-else>
          <!-- 其他文件类型：直接显示链接 -->
          <div class="preview-other">
            <a :href="currentImage" target="_blank">点击下载或查看文件</a>
          </div>
        </template>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import { ref, computed, reactive } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import { maskIdCard } from '../utils/mask'
import config from '@/config'
import { ElMessage } from 'element-plus'
import { getUploadSignature, uploadToOSS, beforeUpload } from '@/utils/uploadUtils'
import { Check } from '@element-plus/icons-vue'

export default {
  name: 'PersonSearch',
  components: {
    Check
  },
  setup() {
    // ---------------- 搜索部分 ----------------
    const searchName = ref('')
    const searchResult = ref(null)
    const loading = ref(false)
    const searched = ref(false)

    const uploading = ref(false);

    const handleSearch = async () => {
      if (!searchName.value.trim()) {
        ElMessage.warning('请输入查询条件')
        return
      }
      loading.value = true
      searched.value = false
      try {
        const response = await axios.get(`${config.BASE_URL}/pc/api/search_application.php`, {
          params: { name: searchName.value.trim() }
        })
        if (response.data.code === 200 && response.data.data) {
          searchResult.value = response.data.data
        } else {
          searchResult.value = null
          ElMessage.error(response.data.message || '未找到数据')
        }
      } catch (error) {
        ElMessage.error('搜索失败，请重试')
      }
      searched.value = true
      loading.value = false
    }
    const maskedIdCard = computed(() => {
      return searchResult.value ? maskIdCard(searchResult.value.idCard) : ''
    })

    const statusTagType = () => {
      return searchResult.value && searchResult.value.status_color
        ? searchResult.value.status_color
        : 'default';
    }


    // ---------------- 新增申请弹窗 ----------------
    const showAddDialog = ref(false)
    const addForm = reactive({
      name: '',
      photo: '',
      gender: '',
      birth: '',
      idCard: '',
      residence: '',
      stages: {} // 格式：{ stageId: { detailId: fileUrl, ... } }
    })
    // 新增申请弹窗中单独使用的阶段子过程数据
    const newStageDetails = reactive({})
    // 阶段分组可以共享
    const stageList = ref([])
    // 新增申请中显示文件名称（简单返回 detailId 字符串）
    const getDetailName = detailId => detailId.toString()
    // 当前激活阶段（新增申请弹窗中），字符串形式
    const activeAddStage = ref('')
    const handleAddStageTabChange = async () => {
      await loadNewStageDetails(activeAddStage.value)
    }
    const handlePhotoUpload = async file => {
      uploading.value = true;

      const valid = await beforeUpload(file, { maxSize: 100 })
      if (!valid) {
        uploading.value = false;
        return;
      }
      try {
        const signature = await getUploadSignature('image')
        const uploadResult = await uploadToOSS(file, signature)
        if (uploadResult && uploadResult.success) {
          addForm.photo = uploadResult.url
        }
      } catch (error) {
        ElMessage.error('照片上传失败')
      } finally {
        uploading.value = false;
      }
    }
    const handleFileChange = async (stageId, detailId, file) => {
      uploading.value = true;  // 开始上传

      const valid = await beforeUpload(file, { maxSize: 200 })
      if (!valid) {
        uploading.value = false;
        return;
      }
      try {
        const signature = await getUploadSignature('file')
        const uploadResult = await uploadToOSS(file, signature)
        if (uploadResult && uploadResult.success) {
          if (!addForm.stages[stageId]) {
            addForm.stages[stageId] = {}
          }
          addForm.stages[stageId][detailId] = uploadResult.url
        }
      } catch (error) {
        ElMessage.error('文件上传失败')
      } finally {
        uploading.value = false;
      }
    }
    const submitApplication = async () => {
      let agent_id = 0
      const user_info = Cookies.get('user_info')
      if (user_info) {
        agent_id = JSON.parse(user_info).agent_id || 0
      }
      const stagesPayload = []
      stageList.value.forEach(stage => {
        const sId = stage.id
        if (addForm.stages[sId]) {
          stagesPayload.push({
            stage_id: sId,
            stage_name: stage.name,
            files: addForm.stages[sId]
          })
        }
      })
      if (!addForm.name || !addForm.gender === '' || !addForm.birth || !addForm.idCard || !addForm.residence) {
        ElMessage.error('请填写完整信息并至少上传一个阶段文件')
        return
      }
      const payload = {
        name: addForm.name,
        photo: addForm.photo,
        gender: addForm.gender,
        birth: addForm.birth,
        idCard: addForm.idCard,
        residence: addForm.residence,
        agent_id,
        stages: stagesPayload
      }
      try {
        const response = await axios.post(`${config.BASE_URL}/pc/api/add_application.php`, payload)
        if (response.data.code === 200) {
          ElMessage.success(response.data.message)
          showAddDialog.value = false
        } else {
          ElMessage.error(response.data.message || '提交失败')
        }
      } catch (error) {
        ElMessage.error('提交失败，请重试')
      }
    }
    const openAddDialog = async () => {
      // 重置新增申请表单
      addForm.name = ''
      addForm.photo = ''
      addForm.gender = ''
      addForm.birth = ''
      addForm.idCard = ''
      addForm.residence = ''
      addForm.stages = {}
      showAddDialog.value = true
      await loadStageList()
      if (stageList.value.length > 0) {
        activeAddStage.value = stageList.value[0].id.toString()
        await loadNewStageDetails(activeAddStage.value)
      }
    }
    // 加载阶段分组定义（适用于新增和查看详情）
    const loadStageList = async () => {
      try {
        let agent_id = 0
        const user_info = Cookies.get('user_info')
        if (user_info) {
          agent_id = JSON.parse(user_info).agent_id || 0
        }
        const response = await axios.get(`${config.BASE_URL}/pc/api/get_stages.php`, {
          params: { agent_id }
        })
        if (response.data.code === 200) {
          stageList.value = response.data.data
        } else {
          ElMessage.error('加载阶段定义失败')
        }
      } catch (error) {
        ElMessage.error('加载阶段定义失败')
      }
    }
    // 加载新增申请弹窗中的阶段详情
    const loadNewStageDetails = async stageId => {
      try {
        let agent_id = 0
        const user_info = Cookies.get('user_info')
        if (user_info) {
          agent_id = JSON.parse(user_info).agent_id || 0
        }
        const response = await axios.get(`${config.BASE_URL}/pc/api/get_stage_details.php`, {
          params: { sg_id: stageId, agent_id }
        })
        if (response.data.code === 200) {
          newStageDetails[stageId] = response.data.data
        } else {
          ElMessage.error('加载新增申请阶段详情失败')
        }
      } catch (error) {
        ElMessage.error('加载新增申请阶段详情失败')
      }
    }

    // ---------------- 查看详情弹窗 ----------------
    const showDetailDialog = ref(false)
    // 查看详情弹窗中当前激活阶段（字符串类型）
    const activeViewStage = ref('')
    // 保存申请记录中的 process 数据（格式：{ "1": { "调解申请书": "fileURL", ... }, ... }）
    const appProcess = reactive({})
    // 查看详情弹窗中使用独立的子过程定义
    const viewStageDetails = reactive({})
    const openDetailDialog = async () => {
      if (searchResult.value && searchResult.value.application_process) {
        try {
          const data = JSON.parse(searchResult.value.application_process)
          // 期望格式：{ stage_id: { stage_name: "...", files: { subProcessName: fileURL } } }
          Object.keys(data).forEach(key => {
            appProcess[key] = data[key].files || {}
          })
        } catch (e) {
          console.error('解析 application_process 失败', e)
          Object.keys(appProcess).forEach(k => delete appProcess[k])
        }
      } else {
        Object.keys(appProcess).forEach(k => delete appProcess[k])
      }
      showDetailDialog.value = true
      await loadStageList()
      if (stageList.value.length > 0) {
        activeViewStage.value = stageList.value[0].id.toString()
        await loadViewStageDetails(stageList.value[0].id)
      }
    }
    // 加载查看详情弹窗中阶段详情
    const loadViewStageDetails = async stageId => {
      try {
        let agent_id = 0
        const user_info = Cookies.get('user_info')
        if (user_info) {
          agent_id = JSON.parse(user_info).agent_id || 0
        }
        const response = await axios.get(`${config.BASE_URL}/pc/api/get_stage_details.php`, {
          params: { sg_id: stageId, agent_id }
        })
        if (response.data.code === 200) {
          viewStageDetails[stageId] = response.data.data
        } else {
          ElMessage.error('加载查看详情阶段详情失败')
        }
      } catch (error) {
        ElMessage.error('加载查看详情阶段详情失败')
      }
    }
    const handleViewStageTabChange = async () => {
      await loadViewStageDetails(activeViewStage.value)
    }
    // 当查看详情弹窗切换阶段时，加载子过程详情（这里复用 handleViewStageTabChange）
    const handleStageTabChange = async () => {
      const stageId = parseInt(activeViewStage.value)
      if (!viewStageDetails[stageId]) {
        await loadViewStageDetails(stageId)
      }
    }
    // 查看详情弹窗中判断某子过程文件是否已上传（匹配子过程名称）
    const hasUploaded = (stageId, subProcessName) => {
      return appProcess[stageId] && appProcess[stageId][subProcessName]
    }
    const getUploaded = (stageId, subProcessName) => {
      return hasUploaded(stageId, subProcessName) ? appProcess[stageId][subProcessName] : ''
    }
    // 查看详情弹窗中文件更新操作
    const handleViewFileUpdate = async (stageId, subProcessName, displayName, file) => {
      uploading.value = true;

      const valid = await beforeUpload(file, { maxSize: 200 })
      if (!valid) {
        uploading.value = false;
        return;
      }
      try {
        const signature = await getUploadSignature('file')
        const uploadResult = await uploadToOSS(file, signature)
        if (uploadResult && uploadResult.success) {
          if (!appProcess[stageId]) {
            appProcess[stageId] = {}
          }
          appProcess[stageId][subProcessName] = uploadResult.url
          await updateApplicationProcess()
          ElMessage.success(`${displayName} 上传成功`)
        }
      } catch (error) {
        ElMessage.error(`${displayName} 上传失败`)
      } finally {
        uploading.value = false;
      }
    }
    // 修改后的 updateApplicationProcess 方法：构造 payload 时加入对应阶段的 stage_name
    const updateApplicationProcess = async () => {
      if (!searchResult.value) return
      try {
        const payloadData = {};
        // 遍历共享阶段分组，若该阶段在 appProcess 中存在则构造包含 stage_name 与 files 的对象
        stageList.value.forEach(stage => {
          if (appProcess[stage.id]) {
            payloadData[stage.id] = {
              stage_name: stage.name,
              files: appProcess[stage.id]
            }
          }
        });
        const payload = {
          id: searchResult.value.id,
          application_process: JSON.stringify(payloadData)
        };
        const response = await axios.post(`${config.BASE_URL}/pc/api/update_application_process.php`, payload);
        if (response.data.code === 200) {
          searchResult.value.application_process = JSON.stringify(payloadData);
          ElMessage.success(response.data.message);
        } else {
          ElMessage.error(response.data.message || '更新申请进程失败');
        }
      } catch (error) {
        ElMessage.error('更新申请进程失败');
      }
    }
    // 新增一个 isStageComplete 方法
    const isStageComplete = (stageId) => {
      if (!viewStageDetails[stageId] || !Array.isArray(viewStageDetails[stageId])) return false;
      return viewStageDetails[stageId].every(detail => hasUploaded(stageId, detail.name));
    }

    // ---------------- 图片预览 ----------------
    // ---------------- 图片/文件预览 ----------------
    const showImageDialog = ref(false)
    const currentImage = ref('')
    const openImage = imgUrl => {
      currentImage.value = imgUrl
      showImageDialog.value = true
    }

    // 根据 currentImage 判断文件类型
    const fileType = computed(() => {
      if (!currentImage.value) return '';
      // 取小写后缀
      const ext = currentImage.value.split('.').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
        return 'image';
      } else if (['mp4', 'webm', 'ogg'].includes(ext)) {
        return 'video';
      } else if (['mp3', 'wav', 'ogg'].includes(ext)) {
        return 'audio';
      } else if (ext === 'pdf') {
        return 'pdf';
      } else if (['doc', 'docx', 'xls', 'xlsx', 'txt'].includes(ext)) {
        // 此处可按需求选择显示 iframe 或者简单下载链接
        return 'other';
      } else {
        return 'other';
      }
    });

    return {
      // 搜索相关
      uploading,
      searchName,
      searchResult,
      loading,
      searched,
      handleSearch,
      maskedIdCard,
      statusTagType,
      // 新增申请弹窗相关
      showAddDialog,
      addForm,
      openAddDialog,
      stageList,
      newStageDetails,
      activeAddStage,
      handleAddStageTabChange,
      handleFileChange,
      handlePhotoUpload,
      getUploadedFile: (stageId, detailId) => addForm.stages[stageId] ? addForm.stages[stageId][detailId] : '',
      submitApplication,
      getDetailName,
      // 查看详情弹窗相关
      showDetailDialog,
      openDetailDialog,
      activeViewStage,
      viewStageDetails,
      handleViewStageTabChange,
      handleStageTabChange,
      hasUploaded,
      getUploaded,
      handleViewFileUpdate,
      updateApplicationProcess,
      isStageComplete,
      // 图片预览
      showImageDialog,
      currentImage,
      openImage,
      fileType,
      // 如果需要调试，也返回 loadStageList, loadNewStageDetails, loadViewStageDetails
      loadStageList,
      loadNewStageDetails,
      loadViewStageDetails
    }
  }
}
</script>

<style scoped>
.search-container {
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #dbe9ff 100%);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-bar {
  display: flex;
  margin-bottom: 20px;
}

.search-input>>>.el-input__inner {
  font-size: 18px;
  height: 46px;
  padding: 0 12px;
}

.search-button {
  font-size: 18px;
  height: 46px;
  padding: 0 20px;
  margin-left: 10px;
}

.add-button {
  font-size: 18px;
  height: 46px;
  padding: 0 20px;
  margin-left: 10px;
  background-color: #67c23a;
  border-color: #67c23a;
}

.loading-container {
  text-align: center;
  margin: 20px 0;
}

.result-container {
  margin-top: 20px;
}

.result-card {
  display: flex;
  padding: 20px;
  border-radius: 12px;
  margin-top: 20px;
}

.result-photo {
  width: 170px;
  height: 170px;
  border-radius: 12px;
  object-fit: cover;
  margin-right: 20px;
}

.result-info {
  flex: 1;
}

.result-info h2 {
  font-size: 28px;
  margin-bottom: 10px;
}

.result-info p {
  font-size: 18px;
  line-height: 1.6;
}

.status-tag {
  font-size: 20px;
  font-weight: bold;
  padding: 16px 12px;
  color: #ffffff;
}

.detail-button {
  margin-left: 20px;
  font-size: 16px;
  padding: 4px 12px;
}

.empty-container {
  text-align: center;
  margin-top: 30px;
}

.detail-dialog ::v-deep .el-dialog__header {
  background-color: #f7f9fc;
  border-bottom: 1px solid #ebeef5;
}

.detail-dialog ::v-deep .el-dialog__title {
  font-size: 18px;
  color: #333;
  font-weight: 600;
}

.detail-dialog-content {
  font-size: 16px;
  line-height: 1.8;
  padding: 10px 0;
  color: #444;
}

.image-dialog ::v-deep .el-dialog__header {
  background-color: #f7f9fc;
  border-bottom: 1px solid #ebeef5;
  font-weight: 600;
}

.image-dialog ::v-deep .el-dialog__body {
  padding: 20px;
  text-align: center;
}

/* 新增预览内容样式 */
.image-preview-container {
  max-height: 70vh;
  overflow: auto;
}

/* 图片预览 */
.preview-img {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  display: block;
  margin: 0 auto;
}

/* 视频预览 */
.preview-video {
  max-width: 100%;
  max-height: 70vh;
  display: block;
  margin: 0 auto;
}

/* 音频预览 */
.preview-audio {
  width: 100%;
  margin: 20px 0;
}

/* PDF 或其他文件预览以 iframe 方式展示 */
.preview-iframe {
  width: 100%;
  height: 70vh;
  border: none;
}

/* 如果是其他文件，居中显示链接 */
.preview-other {
  text-align: center;
  font-size: 16px;
}

.detail-upload {
  margin-bottom: 10px;
}

.stage-completed {
  color: #67c23a;
  /* 亮绿色 */
  font-weight: bold;
}

.completed-icon {
  margin-left: 5px;
  vertical-align: middle;
}

.dialog-footer {
  text-align: right;
}

.button-group {
  display: flex;

  gap: 5px;
}

.completed-button {
  height: 32px;
  line-height: 36px;
  padding: 0 10px;
  vertical-align: middle;
  background-color: rgb(74, 122, 59);
  border: none;
}
</style>
